import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  TASK_KIND_LABELS,
  TASK_KINDS,
  type TaskSummary,
  type TaskTypeView,
} from "@dentacore/shared-types";

import { actOnTask, listTasks, listTaskTypes, snoozeTask } from "../api/tasks";
import { useCan } from "../auth/use-can";
import { across, useShown, type Located } from "../shell/locations";
import { useModules } from "../shell/modules-context";
import { announceRing } from "../shell/quick-ring";
import { useRingProvider } from "../shell/ring/ring-registry";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { NewTaskDialog } from "./new-task-dialog";
import { Holder, KindChip, SourceBadge, WhenCell, recordLink, recordName } from "./task-bits";
import { snoozeChoices, TaskDialog } from "./task-dialog";
import { TaskRulesTab, TaskTypesTab } from "./task-settings";
import styles from "./tasks.module.css";

type Tab = "open" | "snoozed" | "done" | "types" | "rules";
type Lists = Record<"open" | "snoozed" | "done", Located<TaskSummary>[]>;

const EMPTY: Record<"open" | "snoozed" | "done", [string, string]> = {
  open: ["Nothing to do here", "Open tasks for you and your role show here."],
  snoozed: ["No snoozed tasks", "A snoozed task waits here and comes back to Open on its own."],
  done: ["Nothing finished yet", "Resolved and closed tasks show here."],
};

/**
 * **Tasks** (STORY-198c, TK-D1): the team's jobs at the locations shown in the
 * top bar's picker, read one request per location (UR-D11). Open, Snoozed and
 * Done; yours and your roles' unclaimed ones, or everyone's with
 * `tasks.view_all` (TK-D15); and, for `tasks.manage`, the two settings tabs
 * (TK-D35). Drawn as `docs/tasks/prototype/tasks.html`.
 *
 * Only reachable while the clinic has the Tasks preview (TK-D28); the server
 * refuses every route without it, whatever this page draws.
 */
export function TasksPage(): React.JSX.Element {
  const on = useModules().hasPreview("TASKS");
  const can = useCan();
  const shown = useShown();
  const seesAll = can("tasks.view_all");
  const manages = can("tasks.manage");

  const [tab, setTab] = useState<Tab>("open");
  const [view, setView] = useState<"mine" | "all">("mine");
  const [type, setType] = useState<string>("any");
  const [source, setSource] = useState<"any" | "MANUAL" | "AUTO" | "WORKFLOW">("any");
  const [lists, setLists] = useState<Lists | null>(null);
  const [types, setTypes] = useState<TaskTypeView[]>([]);
  const [error, setError] = useState<string | null>(null);
  // A link from Home opens one task (OQ-D12).
  const [searchParams] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(() => searchParams.get("task"));
  /** Where the ring asked the task to open — on *Close*'s reason or the note field. */
  const [openAt, setOpenAt] = useState<"close" | "note" | undefined>(undefined);
  const [adding, setAdding] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!on || shown.ids.length === 0) return;
      const read = (list: "open" | "snoozed" | "done") =>
        across(shown.ids, () => listTasks(list, view, signal));
      Promise.all([read("open"), read("snoozed"), read("done"), listTaskTypes(signal)])
        .then(([open, snoozed, done, kinds]) => {
          setLists({ open, snoozed, done });
          setTypes(kinds);
          setNow(new Date());
          setError(null);
        })
        .catch((cause: unknown) => {
          if (signal?.aborted === true) return;
          setError(cause instanceof Error ? cause.message : "Could not read the tasks");
        });
    },
    [on, shown.key, view],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /*
    ── The quick ring (QR7–QR9, STORY-221) ──────────────────────────────────
    Every action is one the task's dialog or this page already has (QR8): the
    same API call the dialog's button makes, or the dialog itself opened on the
    control — *Close* still asks its reason there. Tasks needs no permission of
    its own (TK-D24); the preview gate hides all of it (TK-D28).
  */
  const navigate = useNavigate();

  /** Open a task's dialog, optionally on one of its controls. */
  const openTask = (id: string, at?: "close" | "note"): void => {
    setOpenAt(at);
    setOpenId(id);
  };

  /** A dialog button's call, from the ring: the list re-reads, the ring says how it went. */
  const act = (work: () => Promise<unknown>, done: string): void => {
    work()
      .then(() => {
        load();
        announceRing(done);
      })
      .catch((cause: unknown) =>
        announceRing(cause instanceof Error ? cause.message : "That did not work"),
      );
  };

  // Blank space: the header's *New task*.
  useRingProvider("page", () =>
    !on || tab === "types" || tab === "rules"
      ? null
      : {
          ring: "page.tasks",
          title: "Tasks",
          meta: lists === null ? undefined : `${String(lists.open.length)} open`,
          actions: [
            {
              key: "new",
              label: "New task",
              icon: RING_ICONS.plus,
              preview: "TASKS",
              hint: "A job for someone on the team",
              run: () => setAdding(true),
            },
          ],
        },
  );

  // One task in the list.
  useRingProvider("tasks.task", (element) => {
    const id = element.dataset.ringId;
    const task =
      lists === null
        ? undefined
        : [...lists.open, ...lists.snoozed, ...lists.done].find((row) => row.id === id);
    if (task === undefined) return null;
    const now = new Date();
    const open = task.status === "OPEN";
    const asleep = task.snoozedUntil !== null && new Date(task.snoozedUntil) > now;
    const tomorrow = snoozeChoices(now).find((choice) => choice.key === "tomorrow");
    const link = recordLink(task);
    return {
      ring: "tasks.task",
      title: task.title,
      meta: `${task.kind !== null ? TASK_KIND_LABELS[task.kind] : (task.type?.name ?? "Task")} · ${
        task.assignee !== null ? task.assignee.name : `for ${task.role?.name ?? "nobody"}`
      }`,
      subject: { type: "task", id: task.id },
      actions: [
        // Slot 1, the next step: claim it while it waits on your role, otherwise finish it.
        open &&
          (task.canTake
            ? {
                key: "take",
                label: "Take it",
                icon: RING_ICONS.hand,
                preview: "TASKS",
                hint: "Makes it yours",
                run: () => act(() => actOnTask(task.id, "take"), "It's yours now."),
              }
            : {
                key: "resolve",
                label: "Resolve",
                icon: RING_ICONS.check,
                preview: "TASKS",
                hint: "It is done",
                run: () => act(() => actOnTask(task.id, "resolve"), "Task resolved."),
              }),
        // A finished task's *Reopen*.
        !open && {
          key: "reopen",
          label: "Reopen",
          icon: RING_ICONS.undo,
          preview: "TASKS",
          hint: "Back to Open",
          run: () => act(() => actOnTask(task.id, "reopen"), "Task reopened."),
        },
        // *Snooze* → *Tomorrow morning*, or *Wake now* for one already asleep.
        open &&
          (asleep
            ? {
                key: "wake",
                label: "Wake now",
                icon: RING_ICONS.snooze,
                preview: "TASKS",
                hint: "Back to Open now",
                run: () => act(() => actOnTask(task.id, "wake"), "Task woken."),
              }
            : tomorrow !== undefined && {
                key: "snooze",
                label: "Snooze",
                icon: RING_ICONS.snooze,
                preview: "TASKS",
                hint: `${tomorrow.label}, ${tomorrow.hint}`,
                run: () =>
                  act(
                    () => snoozeTask(task.id, tomorrow.at.toISOString()),
                    "Snoozed until tomorrow morning.",
                  ),
              }),
        open && {
          key: "note",
          label: "Add note",
          icon: RING_ICONS.note,
          preview: "TASKS",
          hint: "A line for whoever picks it up",
          run: () => openTask(task.id, "note"),
        },
        open && {
          key: "close",
          label: "Close",
          icon: RING_ICONS.close,
          preview: "TASKS",
          danger: true,
          hint: "Not needed, with a reason",
          run: () => openTask(task.id, "close"),
        },
        // The dialog's link to the record it is about (TK-D37).
        link !== null && {
          key: "go",
          label: "Go to it",
          icon: RING_ICONS.link,
          preview: "TASKS",
          hint: link.label,
          run: () => void navigate(link.to),
        },
      ],
    };
  });

  const rows = useMemo(() => {
    if (lists === null || tab === "types" || tab === "rules") return [];
    return lists[tab].filter((t) => {
      if (type !== "any" && (t.kind ?? t.type?.id) !== type) return false;
      if (source !== "any" && t.source !== source) return false;
      return true;
    });
  }, [lists, tab, type, source]);

  if (!on)
    return (
      <div className={styles.page} data-testid="tasks-page">
        <p className={styles.empty}>Tasks is not switched on for this clinic.</p>
      </div>
    );

  const tabButton = (id: Tab, label: string, count?: number) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === id}
      className={tab === id ? `${styles.tab} ${styles.tabOn}` : styles.tab}
      onClick={() => setTab(id)}
      data-testid={`tasks-tab-${id}`}
    >
      {label}
      {count !== undefined && <span className={styles.tabCount}>{count}</span>}
    </button>
  );

  const typeOptions = [
    ...TASK_KINDS.map((k) => ({ value: k, label: TASK_KIND_LABELS[k] })),
    ...types.filter((t) => !t.archived).map((t) => ({ value: t.id, label: t.name })),
  ];

  return (
    <div className={styles.page} data-testid="tasks-page">
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Tasks</h1>
          <p className={styles.lede}>
            Jobs for the team. Some you add yourself, some DentaCore adds when something needs
            doing.
          </p>
        </div>
        <span className={styles.grow} />
        <button
          type="button"
          className={styles.primary}
          onClick={() => setAdding(true)}
          data-testid="tasks-new"
        >
          New task
        </button>
      </header>

      <div className={styles.tabs} role="tablist" aria-label="Which tasks">
        {tabButton("open", "Open", lists?.open.length)}
        {tabButton("snoozed", "Snoozed", lists?.snoozed.length)}
        {tabButton("done", "Done", lists?.done.length)}
        <span className={styles.tabSpacer} />
        {manages && tabButton("rules", "Automatic tasks")}
        {manages && tabButton("types", "Task types")}
      </div>

      {tab === "types" && <TaskTypesTab onChanged={() => load()} />}
      {tab === "rules" && <TaskRulesTab />}

      {tab !== "types" && tab !== "rules" && (
        <>
          <div className={styles.filters}>
            <div className={styles.seg} role="group" aria-label="Whose tasks">
              <button
                type="button"
                className={styles.segButton}
                aria-pressed={view === "mine"}
                onClick={() => setView("mine")}
                data-testid="tasks-view-mine"
              >
                Mine and my roles&rsquo;
              </button>
              {seesAll && (
                <button
                  type="button"
                  className={styles.segButton}
                  aria-pressed={view === "all"}
                  onClick={() => setView("all")}
                  data-testid="tasks-view-all"
                >
                  Everyone
                </button>
              )}
            </div>
            <select
              className={styles.filterSelect}
              aria-label="Type"
              value={type}
              onChange={(event) => setType(event.target.value)}
              data-testid="tasks-filter-type"
            >
              <option value="any">Every type</option>
              {typeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <div className={styles.seg} role="group" aria-label="Manual or automatic">
              {(["any", "MANUAL", "AUTO", "WORKFLOW"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={styles.segButton}
                  aria-pressed={source === value}
                  onClick={() => setSource(value)}
                >
                  {value === "any"
                    ? "All"
                    : value === "MANUAL"
                      ? "Manual"
                      : value === "AUTO"
                        ? "Automatic"
                        : "Workflow"}
                </button>
              ))}
            </div>
          </div>

          {error !== null && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          <div className={styles.list} data-testid="tasks-list">
            {lists !== null && rows.length === 0 && (
              <div className={styles.empty}>
                <span className={styles.emptyTitle}>{EMPTY[tab][0]}</span>
                {EMPTY[tab][1]}
              </div>
            )}
            {rows.map((task) => {
              const place = shown.byId.get(task.at);
              return (
                <div
                  key={task.id}
                  className={styles.row}
                  role="button"
                  tabIndex={0}
                  onClick={() => openTask(task.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openTask(task.id);
                    }
                  }}
                  data-ring="tasks.task"
                  data-ring-id={task.id}
                  data-testid="tasks-row"
                >
                  <KindChip task={task} />
                  <span className={styles.main}>
                    <span className={styles.rowTitle}>
                      {shown.many && place !== undefined && (
                        <span className={styles.place} title={place.name}>
                          {place.code}
                        </span>
                      )}
                      <span className={styles.ellipsis}>{task.title}</span>
                      <SourceBadge task={task} />
                    </span>
                    <span className={styles.sub}>{recordName(task) || task.description}</span>
                  </span>
                  <span className={styles.who}>
                    <Holder task={task} />
                  </span>
                  <WhenCell task={task} now={now} />
                </div>
              );
            })}
          </div>
        </>
      )}

      {openId !== null && (
        <TaskDialog
          key={`${openId}|${openAt ?? ""}`}
          taskId={openId}
          start={openAt}
          onClose={() => setOpenId(null)}
          onChanged={() => load()}
        />
      )}
      {adding && (
        <NewTaskDialog
          types={types.filter((t) => !t.archived)}
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            setTab("open");
            load();
          }}
        />
      )}
    </div>
  );
}
